import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Bell, ChevronRight, Clock, X } from 'lucide-react';
import axios from 'axios';
import Sidebar from './Sidebar';
import { LanguageToggle } from './LanguageToggle';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardLayout = () => {
  const { loading, user } = useAuth();
  const navigate = useNavigate();
  const isClient = user?.user_type === 'client';
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (isClient) {
      axios.get(`${API}/notifications`).then(res => {
        setNotifications(res.data);
      }).catch(() => {});
    }
  }, [isClient]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `hace ${days}d`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white" data-testid="dashboard-layout">
      <Sidebar />
      
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        <header className="h-16 bg-white border-b border-[#467322]/10 flex items-center justify-end px-6 sticky top-0 z-30 gap-4">
          {isClient && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="notifications-bell"
              >
                <Bell size={22} className="text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showDropdown && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50" data-testid="notifications-dropdown">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="font-bold text-gray-900 text-sm">Notificaciones</h3>
                    <button onClick={() => setShowDropdown(false)} className="p-1 rounded-full hover:bg-gray-100">
                      <X size={16} className="text-gray-400" />
                    </button>
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="py-10 text-center">
                        <Bell size={28} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">Sin notificaciones</p>
                      </div>
                    ) : (
                      notifications.slice(0, 5).map((notif) => (
                        <div
                          key={notif.id}
                          className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 last:border-0
                            ${!notif.read ? 'bg-[#467322]/5' : ''}`}
                        >
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!notif.read ? 'bg-[#467322]' : 'bg-transparent'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{notif.title}</p>
                            <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{notif.message}</p>
                            <div className="flex items-center gap-1 mt-1.5">
                              <Clock size={10} className="text-gray-400" />
                              <span className="text-[10px] text-gray-400">{timeAgo(notif.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <button
                      onClick={() => { setShowDropdown(false); navigate('/client/notifications'); }}
                      className="w-full px-4 py-3 text-sm font-semibold text-[#467322] hover:bg-[#F2F7F0] transition-colors flex items-center justify-center gap-1 border-t border-gray-100"
                      data-testid="view-all-notifications"
                    >
                      Ver todas
                      <ChevronRight size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <LanguageToggle variant="default" />
        </header>

        <main className={`flex-1 p-6 lg:p-8 ${isClient ? 'bg-[#FAFAFA]' : 'bg-[#F2F7F0]/30'}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
